import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Gym } from '@/api/gym';

const SELECTED_GYM_KEY = 'selected-gym-storage';

interface GymState {
  gyms: Gym[];
  selectedGymId: string | null;
  isLoading: boolean;
  error: string | null;
  setGyms: (gyms: Gym[]) => void;
  addGym: (gym: Gym) => void;
  removeGym: (gymId: string) => void;
  setSelectedGymId: (gymId: string | null) => void;
  getSelectedGym: () => Gym | undefined;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearGyms: () => void;
}

export const useGymStore = create<GymState>()(
  persist(
    (set, get) => ({
      gyms: [],
      selectedGymId: null,
      isLoading: false,
      error: null,

      setGyms: (gyms) => {
        const state = get();
        // If no gym is selected yet, select the first one
        // If selectedGymId exists but is not in the new gyms list, reset to first gym
        let selectedGymId = state.selectedGymId;
        const isSelectedGymValid = gyms.some((gym) => gym.gymId === selectedGymId);

        if (!selectedGymId || !isSelectedGymValid) {
          selectedGymId = gyms.length > 0 ? gyms[0].gymId : null;
        }

        set({ gyms, error: null, selectedGymId });
      },

      addGym: (gym) =>
        set((state) => ({
          gyms: [...state.gyms, gym],
          // If this is the first gym, select it
          selectedGymId: state.selectedGymId || gym.gymId,
        })),

      removeGym: (gymId) =>
        set((state) => {
          const newGyms = state.gyms.filter((gym) => gym.gymId !== gymId);
          // If the removed gym was selected, select the first available gym
          const newSelectedId =
            state.selectedGymId === gymId
              ? newGyms.length > 0
                ? newGyms[0].gymId
                : null
              : state.selectedGymId;
          return { gyms: newGyms, selectedGymId: newSelectedId };
        }),

      setSelectedGymId: (gymId) => set({ selectedGymId: gymId }),

      getSelectedGym: () => {
        const state = get();
        return state.gyms.find((gym) => gym.gymId === state.selectedGymId);
      },

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      clearGyms: () => set({ gyms: [], selectedGymId: null, error: null }),
    }),
    {
      name: SELECTED_GYM_KEY,
      partialize: (state) => ({
        selectedGymId: state.selectedGymId,
      }),
    }
  )
);

/**
 * Clear gym storage from localStorage
 * Call this on logout to clean up persisted data
 */
export const clearGymStorage = () => {
  localStorage.removeItem(SELECTED_GYM_KEY);
};
